import * as React from 'react';
import { getSupabase } from '@/lib/supabase';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { AuthShell } from '@/components/auth/AuthShell';
import { resolveAuthPortalRole } from '@/utils/resolveAuthPortalRole';
import { syncSessionToServer } from '@/utils/syncSession';
import { readPlanIntentFromQuery, storePlanIntent } from '@/utils/planIntent';
import { signOutOnServer } from '@/data/authApi';

export default function CoachAuth() {
  const routeMode =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/coach/sign-up') || window.location.pathname.startsWith('/coach/signup'))
      ? 'signup'
      : 'signin';

  React.useEffect(() => {
    try {
      localStorage.setItem('coachd:userType', 'coach');
    } catch {
      // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
    }
  }, []);

  // "Choose Pro" on the marketing site links here with ?plan=pro. Captured once
  // on mount (the query string is gone after OAuth bounces through the Worker)
  // and mirrored into localStorage so the Google path can recover it too. It is
  // written into user_metadata at signup and redeemed at the end of onboarding.
  const planIntent = React.useMemo(
    () => (typeof window === 'undefined' ? null : readPlanIntentFromQuery(window.location.search)),
    [],
  );

  React.useEffect(() => {
    if (planIntent) storePlanIntent(planIntent);
  }, [planIntent]);

  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get('portal') === 'mismatch') {
      setError('You started on the client sign-in page. This account is a coach—use coach sign-in below (this page).');
    }
  }, []);

  // Surface email-confirmation failures that AuthCallback redirected here with
  // (?confirm=failed&reason=...). Expired / reused links get friendly copy;
  // everything else falls back to the raw reason so support can triage.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirm') !== 'failed') return;
    const reason = params.get('reason') || '';
    const friendly =
      reason === 'otp_expired'
        ? 'This confirmation link has expired or was already used. Sign in with your password below, or sign up again to receive a new email.'
        : reason === 'email_link_failed'
          ? 'That email link could not be verified. Please sign in with your password, or sign up again to receive a new email.'
          : decodeURIComponent(reason) ||
            'Email confirmation failed. Please sign in with your password or sign up again.';
    setError(friendly);
  }, []);

  const [mode, setMode] = React.useState<'signin' | 'signup' | 'forgot'>(routeMode);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [forgotEmailSent, setForgotEmailSent] = React.useState(false);
  const panelClass = 'space-y-6 rounded-3xl border border-border bg-card p-6 shadow-xl shadow-black/5';
  const inputClass =
    'w-full rounded-xl border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20';
  const primaryButtonClass =
    'w-full rounded-xl bg-sky-600 px-4 py-3 font-medium text-white transition-colors hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50';
  const coachSignInHref = '/coach/sign-in';
  const coachSignUpHref = '/coach/sign-up';
  const legalFooter = (
    <p className="text-center text-xs text-muted-foreground">
      By continuing you agree to our{' '}
      <a
        href="https://os-coach.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="https://os-coach.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-foreground underline underline-offset-2"
      >
        Privacy Policy
      </a>
      .
    </p>
  );

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setLoading(true);
    try {
      // redirectTo must NOT include a `?` query: Supabase's default recovery
      // email template appends `?token_hash=...&type=recovery` to RedirectTo.
      // A value like `/auth/callback?portal=coach` produces a broken URL with
      // two `?` and token_hash is never parsed (user lands in the wrong path).
      // Role / portal for reset still comes from resolveAuthPortalRole + the
      // coachd:userType hint set when this page mounted.
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error: resetError } = await sb.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (resetError) throw resetError;
      setForgotEmailSent(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ provider: 'google', auth_portal: 'coach' });
      window.location.href = `/api/auth/start?${params.toString()}`;
    } catch (e: any) {
      setError(e?.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const sb = getSupabase();
    if (!sb) {
      setError('Auth not configured');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signin') {
        const { data, error } = await sb.auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw error;
        if (data.session) {
          // Await session-sync BEFORE redirect so the cookie is set when
          // the first post-redirect Worker call fires.
          const synced = await syncSessionToServer({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          if (!synced) {
            setError('Sign-in succeeded but we could not sync your session. Please try again.');
            return;
          }
        }
        const signedInUser = data.user;
        if (signedInUser) {
          const role = await resolveAuthPortalRole(sb, signedInUser);
          if (role === 'client') {
            await signOutOnServer();
            await sb.auth.signOut();
            setError('This account is a client. Use client sign-in below.');
            return;
          }
        }
        setSuccess(true);
        // An existing coach can arrive here from a marketing pricing CTA too.
        // Send them to the plan picker rather than straight into Checkout —
        // they may already be subscribed, and surprising an existing customer
        // with a payment page is worse than one extra click.
        setTimeout(() => window.location.replace(planIntent ? '/upgrade' : '/'), 800);
      } else {
        // SPA callback so PKCE code_verifier in localStorage matches email confirmation (same as client Signup)
        const emailRedirectTo = `${window.location.origin}/auth/callback`;
        const { data, error } = await sb.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo,
            data: {
              role: 'coach',
              // Durable copy of the plan they clicked: survives the
              // email-confirmation round trip, which may finish in another tab.
              ...(planIntent ? { intended_plan: planIntent.tier, intended_interval: planIntent.interval } : {}),
            },
          },
        });
        if (error) throw error;
        if (data.user && !data.session) {
          setSuccess(true);
          setError('Please check your email to confirm your account');
        } else if (data.session) {
          const synced = await syncSessionToServer({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          if (!synced) {
            setError('Account created but we could not sync your session. Please sign in.');
            return;
          }
          setSuccess(true);
          setTimeout(() => window.location.replace('/coach/onboarding'), 800);
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      role="coach"
      title={
        mode === 'signin' ? 'Coach Sign In' : mode === 'signup' ? 'Create your coach account' : 'Reset your password'
      }
      subtitle={
        mode === 'signin'
          ? 'Access your coach dashboard, clients, billing, and automations.'
          : mode === 'signup'
            ? 'Create a coach account with a dedicated sign-up link you can share or bookmark.'
            : "We'll email you a link to reset your password."
      }
      footer={legalFooter}
    >
      {success ? (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
          <p className="text-green-400">{error || 'Success! Redirecting...'}</p>
        </div>
      ) : mode === 'forgot' ? (
        <div className={panelClass}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}

          {forgotEmailSent ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10">
                <svg
                  className="h-6 w-6 text-sky-600 dark:text-sky-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-foreground">Check your email</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  If an account exists for <span className="font-medium text-foreground">{email}</span>, we sent a reset
                  link. The link is valid for one hour.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setForgotEmailSent(false);
                  setError('');
                }}
                className="text-sm font-medium text-sky-600 hover:underline dark:text-sky-400"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium mb-2">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" disabled={loading || !email.trim()} className={primaryButtonClass}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setError('');
                }}
                className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      ) : (
        <div className={panelClass}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}

          <GoogleSignInButton onSignIn={handleGoogleSignIn} loading={loading} />

          <AuthDivider />

          <form onSubmit={handlePasswordAuth} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium">
                  Password
                </label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError('');
                      setPassword('');
                      setConfirmPassword('');
                    }}
                    className="text-xs font-medium text-sky-600 hover:underline dark:text-sky-400"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="At least 6 characters"
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            {mode === 'signup' && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Re-enter your password"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <button type="submit" disabled={loading || !email.trim() || !password} className={primaryButtonClass}>
              {loading ? 'Please wait…' : mode === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {mode === 'signin' ? (
              <>
                Don't have an account?{' '}
                <a href={coachSignUpHref} className="font-medium text-sky-600 hover:underline dark:text-sky-400">
                  Sign Up
                </a>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <a href={coachSignInHref} className="font-medium text-sky-600 hover:underline dark:text-sky-400">
                  Sign In
                </a>
              </>
            )}
          </p>

          <p className="border-t border-border pt-2 text-center text-sm text-muted-foreground">
            Signing in as a client?{' '}
            <a href="/client/auth" className="font-medium text-sky-600 hover:underline dark:text-sky-400">
              Client sign in
            </a>
          </p>
        </div>
      )}
    </AuthShell>
  );
}
